import { Button, type ButtonProps } from '@heroui/react'
import type { LinkProps } from 'react-router'
import clsx from 'clsx'
import { useAppSelector } from '@/redux/hooks'
import { useTheme } from '@/ui/theme'

// Allow polymorphic `as={Link}` usage (to/state) on top of ButtonProps.
type FlowButtonProps = ButtonProps & Partial<Pick<LinkProps, 'to' | 'state'>>

// Shared flow action buttons that carry the hoff/misako v1 visual language:
// hoff = black square button, misako = uppercase light-tracking label.

export const FlowPrimaryButton = ({
  children,
  className,
  style,
  ...props
}: FlowButtonProps) => {
  const group = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )
  const theme = useTheme()

  const isHoff = group === 'hoff'
  const isMisako = group === 'misako'

  return (
    <Button
      variant="solid"
      radius="sm"
      size="md"
      fullWidth
      className={clsx(isHoff && 'rounded-xl', className)}
      style={{
        backgroundColor: isHoff ? '#000000' : theme.colors.primary.main,
        color: isHoff ? '#ffffff' : theme.colors.primary.contrastText,
        borderRadius: isHoff ? '0.75rem' : theme.borderRadius,
        ...style
      }}
      {...(props as ButtonProps)}
    >
      {isMisako ? (
        <span className="text-[13px] font-[300] uppercase tracking-wide">
          {children}
        </span>
      ) : (
        children
      )}
    </Button>
  )
}

export const FlowSecondaryButton = ({
  children,
  className,
  style,
  ...props
}: FlowButtonProps) => {
  const group = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )
  const theme = useTheme()

  const isMisako = group === 'misako'

  return (
    <Button
      type="button"
      variant="light"
      radius="sm"
      size="md"
      fullWidth
      className={clsx('underline underline-offset-8', className)}
      style={{ borderRadius: theme.borderRadius, ...style }}
      {...(props as ButtonProps)}
    >
      {isMisako ? (
        <span className="text-[13px] font-[300] uppercase tracking-wide">
          {children}
        </span>
      ) : (
        children
      )}
    </Button>
  )
}
